import { i18n } from '../i18n.js';
/**
 * Portrait feedback for live games.
 *
 * The opponent companion shows the authoritative turn lifecycle:
 *   THINKING -> MOVES! -> THINKING/hidden.
 * Both portraits can temporarily react to health, board, or mana-crystal
 * changes. Local action locking remains owned by the center processing banner.
 */


let ThinkingIndicator = {};

// Hot reloads and Jest re-requires must not leave timers or global listeners behind.
if (ThinkingIndicator && typeof ThinkingIndicator._dispose === 'function') {
    ThinkingIndicator._dispose();
}

ThinkingIndicator = (function () {
    'use strict';

    // Successful moves without a reaction get a brief completion tail; when a
    // reaction exists, that reaction owns the resolved presentation instead.
    const FEEDBACK_TAIL_MS = 2400;
    const BLINK_TICK_MS = 400;
    // After this long in one continuous "Thinking" phase, escalate the label
    // so a storyteller stall never reads as a silent freeze.
    const THINKING_ESCALATE_MS = 20_000;
    const BIG_HIT_THRESHOLD = 5;
    const SIDE_NAMES = ['opponent', 'player'];

    const SIDES = {
        opponent: {
            portraitId: 'opponent-portrait',
            indicatorId: 'opponent-thinking-indicator',
            labelKey: 'game.thinking',
            labelFallback: 'Thinking',
            actLabelKey: 'game.moves',
            actLabelFallback: 'Moves!',
            statusKey: 'game.opponentStatus',
            statusFallback: 'Opponent: {{status}}',
            faceIdle: '(¬‿¬)',
            faceBlink: '(－‿－)',
            faceAct: '(｀∀´)'
        },
        player: {
            portraitId: 'player-portrait',
            indicatorId: 'player-thinking-indicator',
            statusKey: 'game.yourStatus',
            statusFallback: 'You: {{status}}'
        }
    };

    const REACTIONS = {
        bigHit: {
            labelKey: 'game.reactAgony',
            labelFallback: 'That hurt!',
            face: '(×Д×)',
            priority: 70
        },
        trade: {
            labelKey: 'game.reactTrade',
            labelFallback: 'Traded!',
            face: '(@_@)',
            priority: 60
        },
        lost: {
            labelKey: 'game.reactLost',
            labelFallback: 'Lost...',
            face: '(´;ω;`)',
            priority: 55
        },
        manaHit: {
            labelKey: 'game.reactManaHit',
            labelFallback: 'My mana!',
            face: '(◎_◎;)',
            priority: 45
        },
        gotcha: {
            labelKey: 'game.reactGotcha',
            labelFallback: 'Gotcha!',
            face: '(￣ー￣)',
            priority: 40
        },
        hit: {
            labelKey: 'game.reactOuch',
            labelFallback: 'Ouch!',
            face: '(>_<)',
            priority: 30
        },
        healed: {
            labelKey: 'game.reactHealed',
            labelFallback: 'Healed!',
            face: '(＾人＾)',
            priority: 20
        },
        deckoutGloat: {
            labelKey: 'game.reactDeckoutGloat',
            labelFallback: 'Almost empty!',
            face: '😈',
            priority: 90,
            expressionOnly: true
        },
        deckoutAlarm: {
            labelKey: 'game.reactDeckoutAlarm',
            labelFallback: 'My deck!',
            face: '😰',
            priority: 90,
            expressionOnly: true
        }
    };

    const state = {
        phase: null,
        hasWinner: false,
        isYourTurn: false,
        previousView: null,
        acting: false,
        awaitingActionResolution: false,
        actingTimeoutId: null,
        blinkIntervalId: null,
        blinkTick: 0,
        thinkingSince: null,
        escalateTimeoutId: null,
        reactions: {
            opponent: null,
            player: null
        }
    };
    const motionPreference = typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    let reducedMotion = Boolean(motionPreference?.matches);

    function interpolate(template, replacements) {
        return Object.entries(replacements).reduce(
            (text, [name, value]) => text.replaceAll(`{{${name}}}`, String(value)),
            template
        );
    }

    function translate(key, fallback, replacements = {}) {
        const hasI18n = i18n && typeof i18n.t === 'function';
        const translated = hasI18n
            ? (Object.keys(replacements).length
                ? i18n.t(key, replacements)
                : i18n.t(key))
            : null;
        return translated && translated !== key
            ? translated
            : interpolate(fallback, replacements);
    }

    function isGameSurfaceActive() {
        return Boolean(document.body?.classList?.contains('game-active'));
    }

    function isLivePlayingState() {
        return state.phase === 'playing' && !state.hasWinner && isGameSurfaceActive();
    }

    function buildIndicator(side) {
        const config = SIDES[side];
        const indicator = document.createElement('div');
        indicator.id = config.indicatorId;
        indicator.className = 'thinking-indicator';
        indicator.dataset.side = side;

        const aura = document.createElement('span');
        aura.className = 'thinking-aura';
        aura.setAttribute('aria-hidden', 'true');

        const pill = document.createElement('span');
        pill.className = 'thinking-pill';
        pill.setAttribute('aria-hidden', 'true');

        const face = document.createElement('span');
        face.className = 'thinking-face';

        const label = document.createElement('span');
        label.className = 'thinking-label';

        const dots = document.createElement('span');
        dots.className = 'thinking-dots';
        for (let index = 0; index < 3; index++) {
            dots.appendChild(document.createElement('i'));
        }

        pill.appendChild(face);
        pill.appendChild(label);
        pill.appendChild(dots);

        const status = document.createElement('span');
        status.className = 'thinking-status sr-only';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');

        indicator.appendChild(aura);
        indicator.appendChild(pill);
        indicator.appendChild(status);
        return indicator;
    }

    function ensureIndicator(side) {
        const portrait = document.getElementById(SIDES[side].portraitId);
        if (!portrait) return null;

        let indicator = portrait.querySelector('.thinking-indicator');
        if (!indicator) {
            indicator = buildIndicator(side);
            portrait.appendChild(indicator);
        }
        return indicator;
    }

    // Guarded writes prevent blink renders and repeated state broadcasts from
    // re-announcing unchanged text in the polite live region.
    function setText(element, text) {
        if (element.textContent !== text) element.textContent = text;
    }

    function applySide(side, active) {
        const indicator = ensureIndicator(side);
        if (!indicator) return;

        const config = SIDES[side];
        const reaction = active ? state.reactions[side] : null;
        const reacting = Boolean(reaction);
        const acting = !reacting && side === 'opponent' && state.acting && active;

        let label = '';
        let accessibleLabel = '';
        let face = '';
        let stalled = false;
        if (reacting) {
            accessibleLabel = translate(
                reaction.definition.labelKey,
                reaction.definition.labelFallback,
            );
            label = reaction.definition.expressionOnly ? '' : accessibleLabel;
            face = reaction.definition.face;
        } else if (acting) {
            label = translate(config.actLabelKey, config.actLabelFallback);
            accessibleLabel = label;
            face = config.faceAct;
        } else if (active && side === 'opponent') {
            stalled = state.thinkingSince
                && Date.now() - state.thinkingSince >= THINKING_ESCALATE_MS;
            label = stalled
                ? translate('game.thinkingLong', 'Still weaving the tale… this is taking longer than usual.')
                : translate(config.labelKey, config.labelFallback);
            accessibleLabel = label;
            face = state.blinkTick % 6 === 5 ? config.faceBlink : config.faceIdle;
        }

        setText(indicator.querySelector('.thinking-face'), face);
        setText(indicator.querySelector('.thinking-label'), label);
        const statusText = active && accessibleLabel
            ? translate(config.statusKey, config.statusFallback, { status: accessibleLabel })
            : '';
        setText(indicator.querySelector('.thinking-status'), statusText);
        indicator.classList.toggle('is-reacting', reacting);
        indicator.classList.toggle(
            'is-expression-only',
            reacting && reaction.definition.expressionOnly === true,
        );
        indicator.classList.toggle('is-acting', acting);
        indicator.classList.toggle('is-stalled', Boolean(active && stalled));
        indicator.classList.toggle('is-active', active);
        const portrait = indicator.closest('.player-portrait');
        const hudRoot = portrait?.closest('.portrait-container') || portrait;
        if (hudRoot) {
            hudRoot.classList.toggle('thinking-hud-active', active);
        }
    }

    function syncBlinkLoop(opponentBaseActive) {
        if (reducedMotion) {
            stopBlinking();
        } else if (opponentBaseActive && !state.blinkIntervalId) {
            state.blinkIntervalId = setInterval(() => {
                state.blinkTick++;
                render();
            }, BLINK_TICK_MS);
        } else if (!opponentBaseActive && state.blinkIntervalId) {
            clearInterval(state.blinkIntervalId);
            state.blinkIntervalId = null;
        }
    }

    function syncThinkingEscalation(opponentBaseActive) {
        if (opponentBaseActive && !state.thinkingSince) {
            state.thinkingSince = Date.now();
            // One deferred rerender flips the label even under reduced motion,
            // where the blink loop (the usual rerender source) never runs.
            state.escalateTimeoutId = setTimeout(() => {
                state.escalateTimeoutId = null;
                render();
            }, THINKING_ESCALATE_MS);
        } else if (!opponentBaseActive && state.thinkingSince) {
            state.thinkingSince = null;
            if (state.escalateTimeoutId) {
                clearTimeout(state.escalateTimeoutId);
                state.escalateTimeoutId = null;
            }
        }
    }

    function render() {
        const playing = isLivePlayingState();
        const opponentBaseActive = playing && (!state.isYourTurn || state.acting);
        const opponentActive = opponentBaseActive || (playing && Boolean(state.reactions.opponent));
        const playerActive = playing && Boolean(state.reactions.player);

        syncThinkingEscalation(opponentBaseActive);
        applySide('opponent', opponentActive);
        applySide('player', playerActive);
        syncBlinkLoop(opponentBaseActive);
    }

    function stopActing() {
        if (state.actingTimeoutId) {
            clearTimeout(state.actingTimeoutId);
            state.actingTimeoutId = null;
        }
        state.acting = false;
    }

    function flashActing(durationMs) {
        stopActing();
        state.acting = true;
        const timeoutId = setTimeout(() => {
            if (state.actingTimeoutId !== timeoutId) return;
            state.actingTimeoutId = null;
            state.acting = false;
            render();
        }, durationMs);
        state.actingTimeoutId = timeoutId;
    }

    function clearReaction(side) {
        const reaction = state.reactions[side];
        if (!reaction) return;
        if (reaction.timeoutId) clearTimeout(reaction.timeoutId);
        state.reactions[side] = null;
    }

    function clearReactions() {
        SIDE_NAMES.forEach(clearReaction);
    }

    function offerReaction(side, definition) {
        const current = state.reactions[side];
        if (current && current.definition.priority > definition.priority) return;

        if (current?.timeoutId) clearTimeout(current.timeoutId);
        const slot = { definition, timeoutId: null };
        slot.timeoutId = setTimeout(() => {
            if (state.reactions[side] !== slot) return;
            state.reactions[side] = null;
            render();
        }, FEEDBACK_TAIL_MS);
        state.reactions[side] = slot;
    }

    function finiteNumber(value) {
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
    }

    function snapshotBoard(cards) {
        if (!Array.isArray(cards)) return null;
        const ids = [];
        for (const card of cards) {
            if (!card || typeof card.instanceId !== 'string' || card.instanceId.length === 0) {
                return null;
            }
            ids.push(card.instanceId);
        }
        return [...new Set(ids)];
    }

    function snapshotSide(side) {
        if (!side || typeof side !== 'object') return null;
        return {
            health: finiteNumber(side.health),
            totalMana: finiteNumber(side.totalMana),
            boardIds: snapshotBoard(side.summonedCards)
        };
    }

    function snapshotView(gameState) {
        return {
            player: snapshotSide(gameState.player),
            opponent: snapshotSide(gameState.opponent)
        };
    }

    function mergeSideSnapshot(previous, candidate) {
        if (!candidate) return previous || null;
        if (!previous) return candidate;
        return {
            health: candidate.health === null ? previous.health : candidate.health,
            totalMana: candidate.totalMana === null ? previous.totalMana : candidate.totalMana,
            boardIds: candidate.boardIds === null ? previous.boardIds : candidate.boardIds
        };
    }

    function mergeViewSnapshot(previous, candidate) {
        if (!previous) return candidate;
        return {
            player: mergeSideSnapshot(previous.player, candidate.player),
            opponent: mergeSideSnapshot(previous.opponent, candidate.opponent)
        };
    }

    function countRemoved(previousIds, nextIds) {
        if (!Array.isArray(previousIds) || !Array.isArray(nextIds)) return null;
        const next = new Set(nextIds);
        return previousIds.reduce((count, id) => count + (next.has(id) ? 0 : 1), 0);
    }

    function candidateReactions(side, previous, next) {
        const otherSide = side === 'player' ? 'opponent' : 'player';
        const previousSide = previous[side];
        const nextSide = next[side];
        const candidates = [];

        if (previousSide && nextSide) {
            if (previousSide.health !== null && nextSide.health !== null) {
                const healthDelta = nextSide.health - previousSide.health;
                if (healthDelta <= -BIG_HIT_THRESHOLD) {
                    candidates.push(REACTIONS.bigHit);
                } else if (healthDelta < 0) {
                    candidates.push(REACTIONS.hit);
                } else if (healthDelta > 0) {
                    candidates.push(REACTIONS.healed);
                }
            }

            if (
                previousSide.totalMana !== null
                && nextSide.totalMana !== null
                && nextSide.totalMana < previousSide.totalMana
            ) {
                candidates.push(REACTIONS.manaHit);
            }
        }

        const previousOther = previous[otherSide];
        const nextOther = next[otherSide];
        if (previousSide && nextSide && previousOther && nextOther) {
            const ownDeaths = countRemoved(previousSide.boardIds, nextSide.boardIds);
            const otherDeaths = countRemoved(previousOther.boardIds, nextOther.boardIds);
            if (ownDeaths !== null && otherDeaths !== null) {
                if (ownDeaths > 0 && otherDeaths > 0) {
                    candidates.push(REACTIONS.trade);
                } else if (ownDeaths > 0) {
                    candidates.push(REACTIONS.lost);
                } else if (otherDeaths > 0) {
                    candidates.push(REACTIONS.gotcha);
                }
            }
        }

        return candidates;
    }

    function detectReactions(previous, next) {
        for (const side of SIDE_NAMES) {
            const strongest = candidateReactions(side, previous, next)
                .reduce((best, candidate) => (
                    !best || candidate.priority > best.priority ? candidate : best
                ), null);
            if (strongest) offerReaction(side, strongest);
        }
    }

    function showReaction(side, reactionName) {
        const definition = REACTIONS[reactionName];
        if (!SIDE_NAMES.includes(side) || !definition || !isLivePlayingState()) return false;
        offerReaction(side, definition);
        render();
        return true;
    }

    /**
     * Absorb one authoritative player view.
     */
    function sync(gameState) {
        if (!gameState || typeof gameState !== 'object' || typeof gameState.state !== 'string') return;

        state.phase = gameState.state;
        state.hasWinner = Boolean(gameState.winner);
        state.isYourTurn = gameState.isYourTurn === true;

        if (!isLivePlayingState()) {
            stopActing();
            state.awaitingActionResolution = false;
            state.previousView = null;
            clearReactions();
            render();
            return;
        }

        const candidateView = snapshotView(gameState);
        const nextView = mergeViewSnapshot(state.previousView, candidateView);
        if (state.previousView) detectReactions(state.previousView, nextView);
        state.previousView = nextView;

        render();
    }

    /**
     * The authoritative server announces every opponent summon/spell/ability
     * after acceptance and before execution. Hold MOVES! until its explicit
     * ordered settlement; intermediate state broadcasts cannot prove completion.
     */
    function enemyActionStarted() {
        if (!isLivePlayingState() || state.isYourTurn) return;
        state.awaitingActionResolution = true;
        stopActing();
        state.acting = true;
        render();
    }

    function enemyActionSettled(succeeded) {
        if (!state.awaitingActionResolution || typeof succeeded !== 'boolean') return;
        state.awaitingActionResolution = false;
        if (succeeded && !state.reactions.opponent) {
            flashActing(FEEDBACK_TAIL_MS);
        } else {
            stopActing();
            render();
        }
    }

    function stopBlinking() {
        if (!state.blinkIntervalId) return;
        clearInterval(state.blinkIntervalId);
        state.blinkIntervalId = null;
    }

    /* A DEATH HOLDS THE AURA STILL (round 17, 28 September 2026). A death is a
       meaningful combat change, so both portraits react while the card breaks,
       and the aura's sweep (a registered custom property driving a blurred,
       masked conic gradient) and breathe (a box-shadow) repaint the page every
       frame: neither can run on the compositor. With software rendering (a
       desktop whose GPU driver does not match its libraries) the two together
       overran the frame at 1920x1080 at DPR 2 and 2560x1440, a fifth of the
       death's frames at 33-83 ms against 4 in 91 with the aura still. So while
       a death plays, <body data-aura-still> pauses both animations where they
       are (thinking-indicator.css): the aura stays painted, only still, and
       resumes from that frame. The hold ends with the death, never later than
       STILL_CAP_MS after the first hold of a chain, and at a reset (a new
       game); reduced motion has no aura motion to hold.
       Contract: tests/unit/ui/thinkingIndicatorHoldsStillForADeath.test.js. */
    const STILL_CAP_MS = 2000;
    let stillTimer = null;
    let stillSince = 0;

    function holdStill(ms) {
        if (reducedMotion || !document.body) return;
        const now = Date.now();
        if (!stillTimer) stillSince = now;
        const until = Math.min(now + Math.max(0, Number(ms) || 0), stillSince + STILL_CAP_MS);
        clearTimeout(stillTimer);
        document.body.dataset.auraStill = '';
        stillTimer = setTimeout(releaseStill, Math.max(0, until - now));
    }

    function releaseStill() {
        clearTimeout(stillTimer);
        stillTimer = null;
        stillSince = 0;
        if (document.body) delete document.body.dataset.auraStill;
    }

    function reset() {
        releaseStill();
        stopActing();
        stopBlinking();
        clearReactions();
        state.phase = null;
        state.hasWinner = false;
        state.isYourTurn = false;
        state.previousView = null;
        state.awaitingActionResolution = false;
        state.blinkTick = 0;
        render();
    }

    function handleLanguageChange() {
        render();
    }

    function handleMotionChange(event) {
        reducedMotion = Boolean(event?.matches);
        if (reducedMotion) {
            state.blinkTick = 0;
            stopBlinking();
            releaseStill();
        }
        render();
    }

    function _dispose() {
        releaseStill();
        stopActing();
        stopBlinking();
        clearReactions();
        state.awaitingActionResolution = false;
        document.querySelectorAll(
            '.portrait-container.thinking-hud-active, .player-portrait.thinking-hud-active',
        ).forEach((hudRoot) => hudRoot.classList.remove('thinking-hud-active'));
        window.removeEventListener('moonfall:languagechange', handleLanguageChange);
        window.removeEventListener('moonfall:i18nready', handleLanguageChange);
        motionPreference?.removeEventListener?.('change', handleMotionChange);
    }

    window.addEventListener('moonfall:languagechange', handleLanguageChange);
    window.addEventListener('moonfall:i18nready', handleLanguageChange);
    motionPreference?.addEventListener?.('change', handleMotionChange);

    return {
        sync,
        enemyActionStarted,
        enemyActionSettled,
        showReaction,
        holdStill,
        reset,
        _dispose
    };
})();

export { ThinkingIndicator };
